import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  useTheme,
  FormHelperText,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { MuiTelInput } from 'mui-tel-input';

import { browserLogger } from '@widget/utils/datadog';
import { STEPS } from '@toolbox/components/HeaderCommon/CallBackBlock/constants';
import { HintIcon } from '@toolbox/components/HeaderCommon/CallBackBlock/components/HintIcon';
import { sendLeadData } from '@widget/api';
import { EMAIL_REGEX } from '@toolbox/form/validators/regexes';
import { validPhoneByRegexp } from '@toolbox/form/validators/validators';

import type {
  DataTypes,
  StepCommonType,
} from '@toolbox/components/HeaderCommon/CallBackBlock/types';
import type { FC } from 'react';

export type ContactDataStepProps = StepCommonType & {
  contactDataDescription?: string;
  valueForOptions?: { key: string; value: string }[];
  contactDataHint?: string;
  firstNameLabel?: string;
  lastNameLabel?: string;
  phoneLabel?: string;
  emailLabel?: string;
  valueForLabel?: string;
  callbackMessageLabel?: string;
  checkboxLabel?: string;
  defaultErrorMsg?: string;
  contactDataCtaLabel?: string;
  backBtnLabel?: string;
  phoneErrorMsg: string;
  emailErrorMsg: string;
};
export const ContactDataStep: FC<ContactDataStepProps> = ({
  contactDataDescription,
  valueForOptions,
  data,
  setData,
  setStep,
  closeDialog,
  contactDataHint,
  firstNameLabel,
  lastNameLabel,
  phoneLabel,
  emailLabel,
  valueForLabel,
  callbackMessageLabel,
  checkboxLabel,
  defaultErrorMsg,
  contactDataCtaLabel,
  backBtnLabel,
  phoneErrorMsg = 'Telefonnummer ist ungültig',
  emailErrorMsg,
}) => {
  const theme = useTheme();
  const {
    handleSubmit,
    register,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, submitCount, isValid, isSubmitting },
  } = useForm({
    mode: 'onChange',
  });
  const phoneValue = watch('contactForm.phone');
  const [touchedPhone, setTouchePhoned] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [checkboxValue, setCheckboxValue] = useState(checkboxLabel === undefined);

  const onSubmit = async (values: DataTypes) => {
    setData({ ...data, ...values });
    if (!checkboxValue) {
      return;
    }
    try {
      await sendLeadData(values, {
        widgetId: 'SalesAppointments',
        widgetName: 'SalesAppointments',
      });
      setStep(STEPS.CONFIRMATION);
    } catch (error) {
      browserLogger.error(error, 'sales_timeslots book error in CallBack');
      closeDialog();
    }
  };

  const handleInputChange = (value: string) => {
    setValue('contactForm.phone', value);
    const isValid = validPhoneByRegexp(value);
    if (!isValid) {
      setError('contactForm.phone', { message: phoneErrorMsg });
    } else {
      clearErrors('contactForm.phone');
    }
  };

  const disabledCta = submitCount > 0 && (!isValid || !checkboxValue || isSubmitting);
  const errorCheckBox = submitCount > 0 && !checkboxValue;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', overflow: 'auto' }}
    >
      <Box
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            flex: '1 1 auto',
            padding: '32px 32px 0',
            [theme.breakpoints.down('sm')]: {
              padding: '16px 16px 0',
            },
          }}
        >
          <Box mb={{ xs: '16px', sm: '16px', md: '32px' }}>
            <Typography variant={'body1'} component={'h5'} color={'primary'}>
              {contactDataDescription}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: '#F7F8FA',
              display: 'flex',
              padding: '8px',
              gap: '8px',
              marginBottom: { xs: '16px', sm: '16px', md: '32px' },
              color: theme.palette.primary.main,
            }}
          >
            <Box sx={{ flex: '0 0 auto' }}>
              <HintIcon />
            </Box>
            <Typography>{contactDataHint}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridGap: { xs: '16px', sm: '16px', md: '32px' } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridGap: '16px' }}>
              <TextField
                variant={'outlined'}
                label={firstNameLabel}
                {...register('contactForm.firstName', { required: defaultErrorMsg || true })}
                error={errors.contactForm?.firstName !== undefined}
                helperText={errors.contactForm?.firstName?.message}
              />
              <TextField
                variant={'outlined'}
                label={lastNameLabel}
                {...register('contactForm.lastName', { required: defaultErrorMsg || true })}
                error={errors.contactForm?.lastName !== undefined}
                helperText={errors.contactForm?.lastName?.message}
              />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { sm: '1fr', md: '1fr 1fr' },
                gridGap: '16px',
              }}
            >
              <MuiTelInput
                defaultCountry={'DE'}
                label={phoneLabel}
                preferredCountries={['DE', 'AT', 'FR', 'PL', 'ES']}
                {...register('contactForm.phone', {
                  required: defaultErrorMsg || true,
                })}
                onChange={handleInputChange}
                onFocus={() => setTouchePhoned(true)}
                value={phoneValue}
                inputMode="tel"
                fullWidth
                forceCallingCode
                error={!!errors.contactForm?.phone}
                helperText={errors.contactForm?.phone?.message}
                InputProps={{
                  ...(!touchedPhone ? { startAdornment: null } : {}),
                  ref: inputRef,
                }}
                MenuProps={{
                  autoFocus: false,
                  disableAutoFocusItem: true,
                  disableEnforceFocus: true,
                  disableAutoFocus: true,
                  MenuListProps: {
                    sx: {
                      padding: '8px!important',
                    },
                  },
                  marginThreshold: 0,
                  disableScrollLock: true,
                  transitionDuration: { exit: 300, enter: 1300 },
                }}
              />
              <TextField
                variant={'outlined'}
                label={emailLabel}
                {...register('contactForm.email', {
                  required: defaultErrorMsg || true,
                  pattern: {
                    value: EMAIL_REGEX,
                    message: emailErrorMsg || 'Email ist ungültig',
                  },
                })}
                error={errors.contactForm?.email !== undefined}
                helperText={errors.contactForm?.email?.message}
              />
            </Box>
            {!!valueForOptions && valueForOptions?.length > 0 && (
              <FormControl fullWidth error={errors.valueFor !== undefined}>
                <InputLabel id={'value_for'}>{valueForLabel}</InputLabel>

                <Select
                  {...register('valueFor', { required: defaultErrorMsg || true })}
                  labelId={'value_for'}
                  label={valueForLabel}
                  displayEmpty
                >
                  {valueForOptions?.map((option) => (
                    <MenuItem value={option.key} key={option.key}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
                {errors.valueFor !== undefined && (
                  <FormHelperText>{errors.valueFor?.message}</FormHelperText>
                )}
              </FormControl>
            )}
            <TextField
              {...register('callbackMessage')}
              label={callbackMessageLabel}
              multiline
              rows={4}
              maxRows={8}
            />
            {!!checkboxLabel && (
              <FormControl error={errorCheckBox}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxValue}
                      onChange={({ target }) => setCheckboxValue(target.checked)}
                    />
                  }
                  label={checkboxLabel}
                />
                {!!errorCheckBox && (
                  <FormHelperText sx={{ margin: '-5px 0 0' }}>{defaultErrorMsg}</FormHelperText>
                )}
              </FormControl>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'auto 1fr', sm: 'auto 1fr', md: '1fr 1fr' },
          gridGap: { xs: '16px', sm: '16px', md: '32px' },
          padding: '32px',
          [theme.breakpoints.down('sm')]: {
            padding: '16px',
          },
        }}
      >
        <Button
          color={'primary'}
          variant={'outlined'}
          fullWidth
          disableElevation
          onClick={() => setStep(STEPS.SLOTS)}
        >
          {backBtnLabel}
        </Button>
        <Button
          color={'secondary'}
          variant={'contained'}
          type="submit"
          fullWidth
          disableElevation
          disabled={disabledCta}
        >
          {isSubmitting ? (
            <CircularProgress size={'24px'} color={'inherit'} />
          ) : (
            contactDataCtaLabel
          )}
        </Button>
      </Box>
    </form>
  );
};
